import { Link } from 'react-router-dom';
import I18n from 'i18n-js';

import { ELoginWithEmailFormField } from '@lootboy/lib/forms/useLoginWithEmailForm';

import { Divider } from '~/components/atoms/Divider';
import Input from '~/components/atoms/FormControls/Input';
import { PasswordInput } from '~/components/atoms/FormControls/PasswordInput';
import { ScrollContainer } from '~/components/atoms/ScrollContainer';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import { MenuViewHeader } from '~/components/MenuViewHeader';
import { CaptchaOverlay } from '~/components/molecules/CaptchaOverlay';

import { useClosePopup } from '~/hooks/useClosePopup';
import { LoginFooter } from '~/popups/LoginPopup/LoginFooter';
import { SocialLogins } from '~/popups/LoginPopup/SocialLogins';
import {
  LOGIN_INCORRECT_PASSWORD_ERROR,
  useLoginView,
} from '~/popups/LoginPopup/useLoginView';
import { REGISTER_PATHNAME, REQUEST_PASSWORD_RESET_PATH } from '~/routes/paths';

import styles from './LoginPopup.module.scss';

export function LoginPopup(): JSX.Element {
  const closePopup = useClosePopup();

  const { form, captcha, authWithProvider } = useLoginView();

  return (
    <CaptchaOverlay
      showCaptcha={captcha.isRequired}
      onChange={captcha.onChange}
      dismissCaptcha={captcha.dismiss}
    >
      <MenuViewHeader
        title={I18n.t('MenuView_login_bc')}
        onClose={closePopup}
      />
      <ScrollContainer>
        <section className={styles.container}>
          <form
            onSubmit={form.onSubmit}
            noValidate
          >
            <Input
              {...form.register(ELoginWithEmailFormField.EMAIL_OR_USERNAME)}
              error={
                form.errors[ELoginWithEmailFormField.EMAIL_OR_USERNAME]?.message
              }
              label={I18n.t('MenuView_email')}
              id={ELoginWithEmailFormField.EMAIL_OR_USERNAME}
              placeholder={I18n.t('MenuView_email_placeholder')}
              type="email"
            />
            <PasswordInput
              {...form.register(ELoginWithEmailFormField.PASSWORD)}
              error={form.errors[ELoginWithEmailFormField.PASSWORD]?.message}
              label={I18n.t('MenuView_password')}
              id={ELoginWithEmailFormField.PASSWORD}
              placeholder={I18n.t('MenuView_password_placeholder')}
              autoComplete="current-password"
              errorAction={{
                error: LOGIN_INCORRECT_PASSWORD_ERROR,
                component: (
                  <>
                    {I18n.t('MenuView_password_error_wrong_pw')}
                    <Link
                      to={{
                        pathname: REQUEST_PASSWORD_RESET_PATH,
                        state: { email: form.submittedEmail },
                      }}
                      className={styles.link}
                    >
                      {I18n.t('MenuView_forgot_password')}
                    </Link>
                  </>
                ),
              }}
            />
            <StyledButton
              variant={EStyledButtonVariant.BLUE}
              className={styles.loginButton}
              type="submit"
              disabled={!form.isSubmitEnabled}
            >
              {I18n.t('MenuView_continuewithemail')}
            </StyledButton>
          </form>
          <span className={styles.register}>
            {I18n.t('MenuView_no_account')}
            <Link
              to={REGISTER_PATHNAME}
              className={styles.link}
            >
              {I18n.t('MenuView_register_bc')}
            </Link>
          </span>
          <Divider className={styles.divider} />
          <SocialLogins onProviderClick={authWithProvider} />
        </section>
      </ScrollContainer>
      <LoginFooter />
    </CaptchaOverlay>
  );
}
